import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useMemo } from "react";
import { useCurrency } from "@/lib/currency";
import { chargeDisclosure } from "@/lib/booking-display";
import { OG_CORPORATE_IMAGE } from "@/lib/og";

import {
  Landmark,
  Waves,
  Droplets,
  UtensilsCrossed,
  Building2,
  TreePine,
  Drama,
  Mountain,
  Sparkles,
  Heart,
  Music,
  Sun,
  Check,
  Crown,
  Headphones,
  ArrowRight,
  Lock,
  Search,
  X,
  type LucideIcon,
} from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { MAYA_IMAGE_URL as mayaIcon } from "@/lib/maya-art";
import mayaHeroBg from "@/assets/maya-hero-bg.webp.asset.json";
import { getSupabase } from "@/lib/supabase-lazy";
import { useActiveLang } from "@/hooks/use-active-lang";
import { getMayaOffer, MAYA_PRODUCTS } from "@/lib/maya-checkout.functions";
import { getStripeEnvironment, isStripeConfigured } from "@/lib/stripe-browser";

function fmtOfferPrice(p: { amountMinor: number; currency: string; approxMxn: number | null } | undefined, lang: string): string {
  if (!p) return "—";
  const main = new Intl.NumberFormat(lang, { style: "currency", currency: p.currency }).format(p.amountMinor / 100) + " " + p.currency;
  return p.approxMxn ? `${main} · ≈ $${p.approxMxn.toLocaleString(lang)} MXN` : main;
}

const PAGE_SIZE = 6;

const searchSchema = z.object({
  q: fallback(z.string(), "").default(""),
  cat: fallback(z.string(), "").default(""),
  st: fallback(z.string(), "").default(""),
  page: fallback(z.number().int(), 1).default(1),
});

export const Route = createFileRoute("/maya/")({
  validateSearch: zodValidator(searchSchema),
  head: () => {
    const url = "https://www.mostrandomexico.mx/maya";
    const title = "Guías de Maya — México narrado por Maya | Mostrando México";
    const description =
      "Doce universos temáticos, cientos de destinos narrados y una voz que te acompaña. Conoce Maya, la guía digital premium de Mostrando México.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:image", content: OG_CORPORATE_IMAGE },
        { property: "og:image:secure_url", content: OG_CORPORATE_IMAGE },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { name: "twitter:image", content: OG_CORPORATE_IMAGE },

        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: MayaHubPage,
});

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  archaeology: Landmark,
  beaches: Waves,
  cenotes: Droplets,
  gastronomy: UtensilsCrossed,
  "colonial-cities": Building2,
  nature: TreePine,
  culture: Drama,
  adventure: Mountain,
  "pueblos-magicos": Sparkles,
  wellness: Heart,
  nightlife: Music,
  desert: Sun,
};

interface CategoryRow {
  slug: string;
  icon: string | null;
  sort_order: number;
  translations: Record<string, { name?: string; description?: string }> | null;
}

interface DestinationRow {
  id: string;
  slug: string;
  name: string;
  category_slug: string | null;
  state: string | null;
  cover_image_url: string | null;
  is_featured: boolean;
}

function MayaHubPage() {
  const { t } = useTranslation();
  const lang = useActiveLang();
  const { currency, format, rates } = useCurrency();
  const fmtOfferPrice = (p: { amountMinor: number; currency: string } | undefined, locale: string) => p ? `${format(p.amountMinor / 100, p.currency, {locale, decimals:2})} · ${chargeDisclosure(p.amountMinor / 100, p.currency, currency, locale, rates)}` : "—";
  const { q, cat, st, page } = Route.useSearch();
  const navigate = useNavigate({ from: "/maya/" });

  type MayaSearch = { q: string; cat: string; st: string; page: number };
  const setQ = (value: string) =>
    navigate({ search: (prev: MayaSearch) => ({ ...prev, q: value, page: 1 }), replace: true });
  const setCat = (value: string) =>
    navigate({ search: (prev: MayaSearch) => ({ ...prev, cat: value, page: 1 }), replace: true });
  const setSt = (value: string) =>
    navigate({ search: (prev: MayaSearch) => ({ ...prev, st: value, page: 1 }), replace: true });
  const showMore = () =>
    navigate({ search: (prev: MayaSearch) => ({ ...prev, page: (prev.page ?? 1) + 1 }), replace: true });
  const clearAll = () => navigate({ search: { q: "", cat: "", st: "", page: 1 }, replace: true });



  const categoriesQuery = useQuery({
    queryKey: ["maya", "hub", "categories"],
    queryFn: async () => {
      const { data, error } = await (await getSupabase())
        .from("maya_destination_categories")
        .select("slug, icon, sort_order, translations")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as CategoryRow[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const destinationsQuery = useQuery({
    queryKey: ["maya", "hub", "destinations", cat],
    queryFn: async () => {
      let query = (await getSupabase())
        .from("maya_destinations")
        .select("id, slug, name, category_slug, state, cover_image_url, is_featured")
        .eq("status", "published")
        .order("is_featured", { ascending: false })
        .order("sort_order", { ascending: true });
      if (cat) query = query.eq("category_slug", cat);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as DestinationRow[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const offerQuery = useQuery({
    queryKey: ["maya-offer", isStripeConfigured() ? getStripeEnvironment() : "off"],
    queryFn: () => getMayaOffer({ data: { environment: getStripeEnvironment() } }),
    enabled: isStripeConfigured(),
    staleTime: 10 * 60_000,
  });
  const offerSingle = offerQuery.data?.prices[MAYA_PRODUCTS.destination];
  const offerPass = offerQuery.data?.prices[MAYA_PRODUCTS.pass];
  const liveGuides = offerQuery.data?.guidesCount ?? 0;
  const showPassPlan = liveGuides >= 5 && !!offerPass;

  const categories = categoriesQuery.data ?? [];
  const allDestinations = destinationsQuery.data ?? [];

  const categoryCountsQuery = useQuery({
    queryKey: ["maya", "hub", "category-counts"],
    queryFn: async () => {
      const { data, error } = await (await getSupabase())
        .from("maya_destinations")
        .select("category_slug")
        .eq("status", "published");
      if (error) throw error;
      const counts = new Map<string, number>();
      for (const row of (data ?? []) as { category_slug: string | null }[]) {
        if (!row.category_slug) continue;
        counts.set(row.category_slug, (counts.get(row.category_slug) ?? 0) + 1);
      }
      return counts;
    },
    staleTime: 5 * 60 * 1000,
  });
  const categoryCounts = categoryCountsQuery.data ?? new Map<string, number>();


  const availableStates = useMemo(() => {
    const set = new Set<string>();
    for (const d of allDestinations) if (d.state) set.add(d.state);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allDestinations]);

  const filteredDestinations = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return allDestinations.filter((d) => {
      if (st && d.state !== st) return false;
      if (!needle) return true;
      const haystack = `${d.name} ${d.state ?? ""}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [allDestinations, q, st]);

  const currentPage = Math.max(1, page);
  const visibleCount = Math.min(filteredDestinations.length, currentPage * PAGE_SIZE);
  const visibleDestinations = filteredDestinations.slice(0, visibleCount);
  const hasMore = visibleCount < filteredDestinations.length;

  const localizedCategory = (c: CategoryRow) => {
    const tr = c.translations ?? {};
    return tr[lang]?.name || tr.es?.name || tr.en?.name || c.slug;
  };

  const hasFilters = Boolean(q || cat || st);



  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      {/* HERO */}
      <section className="relative isolate overflow-hidden border-b border-border">
        <div
          aria-hidden
          className="absolute inset-0 -z-10 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${mayaHeroBg.url})` }}
        />
        <div
          aria-hidden
          className="absolute inset-0 -z-10 bg-gradient-to-b from-background/60 via-background/85 to-background"
        />
        <div className="absolute -top-24 right-[-10%] -z-10 h-[420px] w-[420px] rounded-full bg-primary/20 blur-[120px]" aria-hidden />

        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 py-16 sm:px-6 sm:py-24 lg:grid-cols-[1.15fr_0.85fr] lg:gap-14 lg:px-8">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
              <Sparkles className="h-3.5 w-3.5" /> {t("mayaHub.heroEyebrow")}
            </span>
            <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-6xl">
              {t("mayaHub.heroTitle")}
            </h1>
            <p className="mt-5 max-w-xl text-base text-muted-foreground sm:text-lg">
              {t("mayaHub.heroSubtitle")}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href="#planes"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-card)] transition hover:opacity-90"
              >
                <Crown className="h-4 w-4" /> {t("mayaHub.heroCtaPrimary")}
              </a>
              <a
                href="#destinos"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-background/80 px-6 py-3 text-sm font-semibold text-foreground backdrop-blur hover:border-primary hover:text-primary"
              >
                {t("mayaHub.heroCtaSecondary")} <ArrowRight className="h-4 w-4" />
              </a>
            </div>

            <dl className="mt-10 grid max-w-md grid-cols-3 gap-4 border-t border-border/60 pt-6">
              <div>
                <dt className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  {t("mayaHub.statsUniverses")}
                </dt>
                <dd className="mt-1 font-display text-2xl font-semibold">{categories.length || "—"}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  {t("mayaHub.statsDestinations")}
                </dt>
                <dd className="mt-1 font-display text-2xl font-semibold">
                  {allDestinations.length > 0 ? allDestinations.length : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  {t("mayaHub.statsLanguages")}
                </dt>
                <dd className="mt-1 font-display text-2xl font-semibold">5</dd>
              </div>
            </dl>
          </div>

          <div className="relative mx-auto w-full max-w-sm lg:mx-0 lg:justify-self-end">
            <div className="absolute inset-0 -z-10 rounded-[2rem] bg-gradient-to-br from-primary/30 to-primary/5 blur-2xl" aria-hidden />
            <div className="rounded-[2rem] border border-primary/30 bg-card/70 p-8 shadow-[var(--shadow-card)] backdrop-blur-xl">
              <div className="flex items-center gap-4">
                <img loading="lazy" decoding="async"
                  src={mayaIcon}
                  alt="Maya"
                  width={72}
                  height={72}
                  className="h-[72px] w-[72px] rounded-full border-2 border-primary/40 object-cover"
                />
                <div>
                  <div className="font-display text-xl font-semibold">Maya</div>
                  <div className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
                    {t("mayaHome.mayaRole")}
                  </div>
                </div>
              </div>
              <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
                "{t("mayaHome.sectionDescription").replace(/"/g, "")}"
              </p>
              <div className="mt-6 flex items-center gap-2 rounded-full border border-border bg-background/70 px-4 py-2 text-xs text-muted-foreground">
                <Headphones className="h-4 w-4 text-primary" />
                <span>{t("mayaHome.ctaSample")}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section id="universos" className="border-b border-border bg-surface">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              {t("mayaHub.categoriesEyebrow")}
            </div>
            <h2 className="mt-2 font-display text-3xl font-semibold sm:text-4xl">
              {t("mayaHub.categoriesTitle")}
            </h2>
            <p className="mt-3 text-muted-foreground">
              {t("mayaHub.categoriesDescription")}
            </p>
          </div>

          <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4">
            {(categoriesQuery.isLoading ? Array.from({ length: 12 }) : categories).map(
              (c, i) => {
                if (!c) {
                  return (
                    <div
                      key={i}
                      className="h-28 animate-pulse rounded-2xl border border-border bg-card"
                    />
                  );
                }
                const catRow = c as CategoryRow;
                const Icon = CATEGORY_ICONS[catRow.slug] ?? Sparkles;
                const count = categoryCounts.get(catRow.slug) ?? 0;
                const isActive = cat === catRow.slug;
                const isEmpty = count === 0;
                return (
                  <button
                    key={catRow.slug}
                    type="button"
                    onClick={() => setCat(isActive ? "" : catRow.slug)}
                    aria-pressed={isActive}
                    className={[
                      "group relative overflow-hidden rounded-2xl border p-5 text-left shadow-[var(--shadow-card)] transition-all hover:-translate-y-0.5 hover:shadow-lg",
                      isActive
                        ? "border-primary bg-primary/10"
                        : "border-border bg-card hover:border-primary/50",
                    ].join(" ")}
                  >
                    <div className={[
                      "absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-primary/0 via-primary to-primary/0 transition-opacity",
                      isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100",
                    ].join(" ")} />
                    <span className={[
                      "grid h-11 w-11 place-items-center rounded-xl transition-colors",
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground",
                    ].join(" ")}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <div className="mt-4 font-display text-base font-semibold leading-tight">
                      {localizedCategory(catRow)}
                    </div>
                    <div className="mt-1 text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                      {isEmpty
                        ? t("mayaHub.comingSoon")
                        : t("mayaHub.resultsCount", { count })}
                    </div>
                  </button>
                );
              },
            )}

          </div>
        </div>
      </section>

      {/* DESTINATIONS */}
      <section id="destinos" className="border-b border-border">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="max-w-2xl">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                {t("mayaHub.destinationsEyebrow")}
              </div>
              <h2 className="mt-2 font-display text-3xl font-semibold sm:text-4xl">
                {t("mayaHub.destinationsTitle")}
              </h2>
              <p className="mt-3 text-muted-foreground">
                {t("mayaHub.destinationsDescription")}
              </p>
            </div>
          </div>

          {/* Search + active filter toolbar */}
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-md">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("mayaHub.searchPlaceholder")}
                aria-label={t("mayaHub.searchPlaceholder")}
                className="w-full rounded-full border border-border bg-card py-2.5 pl-10 pr-10 text-sm shadow-[var(--shadow-card)] outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
              {q && (
                <button
                  type="button"
                  onClick={() => setQ("")}
                  aria-label="Limpiar búsqueda"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <div className="relative">
                <select
                  value={st}
                  onChange={(e) => setSt(e.target.value)}
                  aria-label={t("mayaHub.filterByState")}
                  className="appearance-none rounded-full border border-border bg-card px-4 py-2 pr-8 text-xs font-medium text-foreground shadow-[var(--shadow-card)] outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                >
                  <option value="">{t("mayaHub.allStates")}</option>
                  {availableStates.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <ArrowRight className="pointer-events-none absolute right-2.5 top-1/2 h-3 w-3 -translate-y-1/2 rotate-90 text-muted-foreground" />
              </div>
              {cat && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 font-medium text-primary">
                  {(() => {
                    const active = categories.find((c) => c.slug === cat);
                    return active ? localizedCategory(active) : cat;
                  })()}
                  <button
                    type="button"
                    onClick={() => setCat("")}
                    aria-label="Quitar categoría"
                    className="rounded-full hover:bg-primary/20"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              {st && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 font-medium text-primary">
                  {st}
                  <button
                    type="button"
                    onClick={() => setSt("")}
                    aria-label="Quitar estado"
                    className="rounded-full hover:bg-primary/20"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              <span>
                {t("mayaHub.resultsCount", { count: filteredDestinations.length })}
              </span>
              {hasFilters && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="rounded-full px-2.5 py-1 font-medium text-primary hover:bg-primary/10"
                >
                  {t("mayaHub.clearFilters")}
                </button>
              )}
            </div>
          </div>


          <div className="mt-8">
            {destinationsQuery.isLoading ? (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-72 animate-pulse rounded-2xl border border-border bg-card"
                  />
                ))}
              </div>
            ) : filteredDestinations.length === 0 ? (
              <div className="grid place-items-center rounded-3xl border border-dashed border-border bg-card/50 px-6 py-16 text-center">
                <div className="grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary">
                  <Sparkles className="h-6 w-6" />
                </div>
                <p className="mt-4 max-w-md text-muted-foreground">
                  {hasFilters ? t("mayaHub.noResults") : t("mayaHub.destinationsEmpty")}
                </p>
                {hasFilters && (
                  <button
                    type="button"
                    onClick={clearAll}
                    className="mt-4 inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold hover:border-primary hover:text-primary"
                  >
                    {t("mayaHub.clearFilters")}
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {visibleDestinations.map((d) => (
                    <Link
                      key={d.id}
                      to="/maya/$slug"
                      params={{ slug: d.slug }}
                      className="group overflow-hidden rounded-2xl border border-border bg-card shadow-[var(--shadow-card)] transition-all hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-lg"
                    >
                      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                        {d.cover_image_url ? (
                          <img
                            src={d.cover_image_url}
                            alt={d.name}
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div className="grid h-full w-full place-items-center text-muted-foreground">
                            <Sparkles className="h-8 w-8" />
                          </div>
                        )}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                          <div className="text-xs font-medium uppercase tracking-[0.14em] text-white/80">
                            {d.state}
                          </div>
                          <div className="font-display text-lg font-semibold text-white">
                            {d.name}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between px-5 py-4 text-sm">
                        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                          <Headphones className="h-4 w-4 text-primary" />
                          {t("mayaHome.feat6")}
                        </span>
                        <ArrowRight className="h-4 w-4 text-primary transition-transform group-hover:translate-x-0.5" />
                      </div>
                    </Link>
                  ))}
                </div>

                <div className="mt-10 flex flex-col items-center gap-3">
                  <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                    {t("mayaHub.showingCount", {
                      shown: visibleDestinations.length,
                      total: filteredDestinations.length,
                    })}
                  </p>
                  {hasMore && (
                    <button
                      type="button"
                      onClick={showMore}
                      className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-6 py-3 text-sm font-semibold text-primary transition hover:bg-primary hover:text-primary-foreground"
                    >
                      {t("mayaHub.showMore")}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </>
            )}

          </div>
        </div>
      </section>

      {/* PLANS */}
      <section id="planes" className="border-b border-border bg-surface">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              {t("mayaHub.plansEyebrow")}
            </div>
            <h2 className="mt-2 font-display text-3xl font-semibold sm:text-4xl">
              {t("mayaHub.plansTitle")}
            </h2>
            <p className="mt-3 text-muted-foreground">{t("mayaHub.plansDescription")}</p>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-2">
            <PlanCard
              name={t("mayaHub.planSingleName")}
              price={fmtOfferPrice(offerSingle, lang)}
              tag={t("mayaHub.planSingleTag")}
              desc={t("mayaHub.planSingleDesc")}
              cta={t("mayaHub.ctaBuySingle")}
              includes={[t("mayaHome.feat1"), t("mayaHome.feat3"), t("mayaHome.feat4")]}
              includesLabel={t("mayaHub.planIncludes")}
              href="#destinos"
            />
            {showPassPlan && (
            <PlanCard
              featured
              ribbon={t("mayaHub.planPassRibbon")}
              name={t("mayaHub.planPassName")}
              price={fmtOfferPrice(offerPass, lang)}
              tag={t("mayaHub.planPassTag")}
              desc={t("mayaHub.planPassDesc", { count: liveGuides })}
              cta={t("mayaHub.ctaBuyPass")}
              includes={[
                t("mayaHome.feat1"),
                t("mayaHome.feat2"),
                t("mayaHome.feat3"),
                t("mayaHome.feat4"),
                t("mayaHome.feat5"),
                t("mayaHome.feat6"),
              ]}
              includesLabel={t("mayaHub.planIncludes")}
              href="#destinos"
            />
            )}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primary/15 via-background to-background" aria-hidden />
        <div className="mx-auto max-w-4xl px-4 py-20 text-center sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-semibold sm:text-5xl">
            {t("mayaHub.finalTitle")}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            {t("mayaHub.finalDesc")}
          </p>
          <a
            href="#planes"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-card)] hover:opacity-90"
          >
            <Crown className="h-4 w-4" /> {t("mayaHub.finalCta")}
          </a>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function PlanCard({
  name,
  price,
  tag,
  desc,
  cta,
  includes,
  includesLabel,
  href,
  featured,
  ribbon,
}: {
  name: string;
  price: string;
  tag: string;
  desc: string;
  cta: string;
  includes: string[];
  includesLabel: string;
  href: string;
  featured?: boolean;
  ribbon?: string;
}) {
  return (
    <div
      className={[
        "relative flex flex-col rounded-3xl p-7 shadow-[var(--shadow-card)] transition-all",
        featured
          ? "border-2 border-primary bg-gradient-to-br from-primary/10 via-card to-card"
          : "border border-border bg-card",
      ].join(" ")}
    >
      {featured && ribbon && (
        <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
          <Crown className="h-3 w-3" /> {ribbon}
        </span>
      )}
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="font-display text-xl font-semibold">{name}</h3>
        <div className="text-right">
          <div className={`font-display text-2xl font-bold ${featured ? "text-primary" : ""}`}>
            {price}
          </div>
          <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
            {tag}
          </div>
        </div>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">{desc}</p>

      <div className="mt-6 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {includesLabel}
      </div>
      <ul className="mt-3 space-y-2 text-sm">
        {includes.map((line) => (
          <li key={line} className="flex items-start gap-2">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            {line}
          </li>
        ))}
      </ul>

      <a
        href={href}
        className={[
          "mt-7 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition",
          featured
            ? "bg-primary text-primary-foreground hover:opacity-90"
            : "border border-border bg-background text-foreground hover:border-primary hover:text-primary",
        ].join(" ")}
      >
        {featured ? <Crown className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
        {cta}
      </a>
    </div>
  );
}
